import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Loader2, Award, Clock } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AD_LEAD_QUALITY_REPORT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import {
    AdFilterBar, AttributionBanner, EmptyState, StatTile, money, num, pct,
    resolveDateRange,
    StatusPill, isDelivering,
    StatusFilterSelect,
} from "./adReportKit";

const scoreTone = (v) => {
    if (v == null) return "text-muted-foreground";
    if (v >= 70) return "text-emerald-600 dark:text-emerald-400";
    if (v >= 45) return "text-amber-600 dark:text-amber-400";
    return "text-red-600 dark:text-red-400";
};

const POTENTIAL_TONE = {
    Hot: "bg-red-500", Medium: "bg-amber-500", Cold: "bg-blue-400",
};

/** Hot / Medium / Cold mix as a single stacked bar. */
const PotentialMix = ({ potential }) => {
    const entries = Object.entries(potential || {});
    const total = entries.reduce((a, [, n]) => a + n, 0);
    if (!total) return <span className="text-[11px] text-muted-foreground">—</span>;
    return (
        <div className="flex h-1.5 w-full min-w-[60px] overflow-hidden rounded-full bg-muted"
            title={entries.map(([k, n]) => `${k}: ${n}`).join(" · ")}>
            {["Hot", "Medium", "Cold"].map((k) => (
                potential[k] ? (
                    <div key={k} className={POTENTIAL_TONE[k]}
                        style={{ width: `${(potential[k] * 100) / total}%` }} />
                ) : null
            ))}
        </div>
    );
};

/** Hours are unreadable past a day or two; show the unit a rep would use. */
const humanHours = (h) => {
    if (h == null) return "—";
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 48) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
};

export default function AdLeadQualityReport() {
    const { fetch } = useApiMethods();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [datePreset, setDatePreset] = usePersistedState("ad_lq_preset", "last_90");
    const [customFrom, setCustomFrom] = usePersistedState("ad_lq_from", "");
    const [customTo, setCustomTo] = usePersistedState("ad_lq_to", "");
    const [accountId, setAccountId] = usePersistedState("ad_lq_account", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ad_lq_status", "");

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams();
        if (from_date) p.append("from_date", from_date);
        if (to_date) p.append("to_date", to_date);
        if (accountId) p.append("account_id", accountId);
        if (statusFilter) p.append("status", statusFilter);
        fetch(`${AD_LEAD_QUALITY_REPORT_URL}?${p.toString()}`)
            .then((res) => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load report");
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, accountId, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const rows = data?.data || [];
    const s = data?.summary;
    const wired = data?.attribution?.is_wired;

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Award className="h-6 w-6 text-primary" />
                            Lead Quality by Ad
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Not how many leads an ad produced — how good they turned out to be.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                <AdFilterBar
                    {...{ datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo,
                        accountId, setAccountId, accounts: data?.filters?.accounts || [] }}
                    >
                        <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                </AdFilterBar>

                <AttributionBanner attribution={data?.attribution} />

                {wired && s && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatTile tone="leads" label="Attributed leads" value={num(s.leads)}
                            sub={`across ${s.ads} ads`} />
                        <StatTile label="Average quality" value={s.avg_quality_score ?? "—"}
                            sub="0–100, weighted by leads" />
                        <StatTile tone="cost" label="Time to first contact"
                            value={humanHours(s.avg_hours_to_contact)}
                            sub="average across all leads" />
                        <StatTile label="Best ad" value={s.best ? "▲" : "—"}
                            sub={s.best || null} />
                    </div>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && wired && rows.length === 0 && (
                    <EmptyState title="No attributed leads in this period" />
                )}

                {!loading && wired && rows.length > 0 && (
                    <>
                        <div className="overflow-x-auto rounded-lg border border-border">
                            <table className="w-full text-xs">
                                <thead className="bg-muted/50">
                                    <tr className="text-muted-foreground">
                                        <th className="px-3 py-2.5 text-left font-medium min-w-[200px]">Ad</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Score</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Leads</th>
                                        <th className="px-2 py-2.5 text-right font-medium">CPL</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Reached demo</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Disqualified</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Never contacted</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Avg unanswered</th>
                                        <th className="px-2 py-2.5 text-right font-medium">To 1st contact</th>
                                        <th className="px-2 py-2.5 text-right font-medium">AI score</th>
                                        <th className="px-2 py-2.5 text-left font-medium">Hot/Med/Cold</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {rows.map((r) => (
                                        <tr key={r.ad_id} className={cn("hover:bg-accent/30",
                                            !isDelivering(r.effective_status) && "opacity-60 bg-muted/20")}>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-2">
                                                    {r.thumbnail_url && (
                                                        <img src={r.thumbnail_url} alt=""
                                                            className="h-7 w-7 rounded object-cover shrink-0 border border-border"
                                                            onError={(e) => { e.target.style.display = "none"; }} />
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="text-foreground truncate max-w-[170px]" title={r.name}>{r.name}</p>
                                                        <div className="flex items-center gap-1.5">
                                                            <StatusPill status={r.effective_status} compact />
                                                            {r.campaign_name && (
                                                                <p className="text-[10px] text-muted-foreground truncate max-w-[105px]">
                                                                    {r.campaign_name}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                <span className={cn("text-sm font-bold tabular-nums", scoreTone(r.quality_score))}>
                                                    {r.quality_score}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2 text-right text-foreground tabular-nums">{num(r.leads)}</td>
                                            <td className="px-2 py-2 text-right text-amber-700 dark:text-amber-400 tabular-nums">{money(r.cpl)}</td>
                                            <td className="px-2 py-2 text-right text-emerald-600 dark:text-emerald-400 font-medium tabular-nums">{pct(r.demo_rate, 1)}</td>
                                            <td className="px-2 py-2 text-right text-red-600 dark:text-red-400 tabular-nums">{pct(r.disqualified_rate, 1)}</td>
                                            <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{pct(r.never_contacted_rate, 1)}</td>
                                            <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{r.avg_unanswered_count ?? "—"}</td>
                                            <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{humanHours(r.avg_hours_to_contact)}</td>
                                            <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{r.avg_lead_score ?? "—"}</td>
                                            <td className="px-2 py-2 min-w-[70px]"><PotentialMix potential={r.potential} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="rounded-lg border border-border bg-card p-3 text-[11px] text-muted-foreground space-y-1">
                            <p className="font-medium text-foreground">How the score is built</p>
                            <p>
                                55% share of leads reaching a demo, 25% not disqualified, 20% someone
                                actually made contact. Deliberately simple — every component is a column
                                on this table, so a score can always be explained rather than trusted.
                            </p>
                            <p className="flex items-center gap-1.5 pt-1">
                                <Clock className="h-3 w-3" />
                                Time to first contact is measured from lead creation to the first status-log entry.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </ContainerCard>
    );
}
